import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGame } from '../../contexts/GameContext';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const MainNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProgressionOpen, setIsProgressionOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { getUserStats, currentStreak } = useGame();

  const userStats = getUserStats();

  const navigationItems = [
    { label: 'Main Menu', path: '/main-menu', icon: 'Home', unlocked: true },
    { label: 'Game Arena', path: '/game-arena', icon: 'Gamepad2', unlocked: true },
    { label: 'AI Mood', path: '/ai-mood-selection', icon: 'Brain', unlocked: true },
    { label: 'Gem Collection', path: '/gem-collection', icon: 'Gem', unlocked: true },
  ];

  const progressionData = {
    gems: userStats?.totalGems || 0,
    currentStreak: currentStreak || 0,
    bestStreak: userStats?.bestStreak || 0,
    unlockedMoods: 3,
    totalMoods: 5,
    notifications: 0,
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProgression = () => {
    setIsProgressionOpen(!isProgressionOpen);
  };

  const isActive = (path) => location?.pathname === path;

  return (
    <>
      <header className="main-navigation fixed top-0 left-0 right-0 h-16 bg-card border-b-2 border-primary z-[1000] shadow-glow-cyan-md">
        <div className="h-full px-base flex items-center justify-between">
          <div className="main-navigation-logo flex items-center gap-md">
            <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center border border-primary shadow-glow-cyan-sm transition-all duration-base hover:shadow-glow-cyan-md">
              <Icon name="Zap" size={24} color="var(--color-primary)" />
            </div>
            <span className="hidden sm:block text-xl font-heading font-bold neon-text-cyan">
              CyberOddEven
            </span>
          </div>

          <nav className="hidden lg:flex items-center gap-sm flex-1 justify-center">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  px-lg py-sm rounded-sm font-caption text-sm tracking-wide
                  transition-all duration-base touch-target
                  ${isActive(item?.path)
                    ? 'bg-primary/20 text-primary border border-primary shadow-glow-cyan-sm'
                    : 'text-foreground hover:text-primary hover:bg-primary/10 border border-transparent'
                  }
                  ${!item?.unlocked ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                `}
                onClick={(e) => !item?.unlocked && e?.preventDefault()}
              >
                <div className="flex items-center gap-sm">
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                  {!item?.unlocked && (
                    <Icon name="Lock" size={14} />
                  )}
                </div>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-sm">
            <div className="hidden md:block relative">
              <button
                onClick={toggleProgression}
                className="px-lg py-sm rounded-sm bg-card border border-primary/50 hover:border-primary transition-all duration-base touch-target flex items-center gap-md shadow-glow-cyan-sm hover:shadow-glow-cyan-md"
              >
                <div className="flex items-center gap-xs">
                  <Icon name="Gem" size={18} color="var(--color-accent)" />
                  <span className="font-data text-accent">{progressionData?.gems}</span>
                </div>
                <div className="w-px h-4 bg-primary/30" />
                <div className="flex items-center gap-xs">
                  <Icon name="Flame" size={18} color="var(--color-warning)" />
                  <span className="font-data text-warning">{progressionData?.currentStreak}</span>
                </div>
                {progressionData?.notifications > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-error rounded-sm flex items-center justify-center border border-error shadow-glow-magenta-sm">
                    <span className="text-xs font-bold text-error-foreground">{progressionData?.notifications}</span>
                  </div>
                )}
              </button>

              {isProgressionOpen && (
                <div className="absolute top-full right-0 mt-sm w-72 bg-card border-2 border-primary rounded-sm shadow-glow-cyan-lg z-[1100] p-lg">
                  <div className="space-y-md">
                    <div className="flex items-center justify-between pb-sm border-b border-primary/30">
                      <h3 className="font-heading font-semibold text-primary">Player Stats</h3>
                      <button onClick={toggleProgression}>
                        <Icon name="X" size={18} color="var(--color-primary)" />
                      </button>
                    </div>
                    
                    <div className="space-y-sm">
                      <div className="flex items-center justify-between p-sm bg-background rounded-sm border border-accent/30">
                        <div className="flex items-center gap-sm">
                          <Icon name="Gem" size={20} color="var(--color-accent)" />
                          <span className="font-caption text-sm">Total Gems</span>
                        </div>
                        <span className="font-data text-lg text-accent">{progressionData?.gems}</span>
                      </div>

                      <div className="flex items-center justify-between p-sm bg-background rounded-sm border border-warning/30">
                        <div className="flex items-center gap-sm">
                          <Icon name="Flame" size={20} color="var(--color-warning)" />
                          <span className="font-caption text-sm">Current Streak</span>
                        </div>
                        <span className="font-data text-lg text-warning">{progressionData?.currentStreak}</span>
                      </div>

                      <div className="flex items-center justify-between p-sm bg-background rounded-sm border border-secondary/30">
                        <div className="flex items-center gap-sm">
                          <Icon name="Trophy" size={20} color="var(--color-secondary)" />
                          <span className="font-caption text-sm">Best Streak</span>
                        </div>
                        <span className="font-data text-lg text-secondary">{progressionData?.bestStreak}</span>
                      </div>

                      <div className="flex items-center justify-between p-sm bg-background rounded-sm border border-primary/30">
                        <div className="flex items-center gap-sm">
                          <Icon name="Brain" size={20} color="var(--color-primary)" />
                          <span className="font-caption text-sm">AI Moods</span>
                        </div>
                        <span className="font-data text-lg text-primary">
                          {progressionData?.unlockedMoods}/{progressionData?.totalMoods}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="outline"
              size="default"
              iconName="LogOut"
              iconPosition="left"
              onClick={handleLogout}
              className="hidden md:flex"
            >
              Logout
            </Button>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-sm rounded-sm border border-primary bg-primary/20 shadow-glow-cyan-sm hover:shadow-glow-cyan-md transition-all duration-base touch-target"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} color="var(--color-primary)" />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-background z-[900] lg:hidden pt-16">
          <nav className="flex flex-col p-base gap-sm h-full overflow-y-auto terminal-scrollbar">
            <div className="mb-lg p-lg bg-card border-2 border-primary rounded-sm shadow-glow-cyan-md">
              <h3 className="font-heading font-semibold text-primary mb-md">Player Stats</h3>
              <div className="space-y-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-sm">
                    <Icon name="Gem" size={18} color="var(--color-accent)" />
                    <span className="font-caption text-sm">Gems</span>
                  </div>
                  <span className="font-data text-accent">{progressionData?.gems}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-sm">
                    <Icon name="Flame" size={18} color="var(--color-warning)" />
                    <span className="font-caption text-sm">Streak</span>
                  </div>
                  <span className="font-data text-warning">{progressionData?.currentStreak}</span>
                </div>
              </div>
            </div>

            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`
                  px-lg py-base rounded-sm font-caption text-base tracking-wide
                  transition-all duration-base touch-target
                  ${isActive(item?.path)
                    ? 'bg-primary/20 text-primary border-2 border-primary shadow-glow-cyan-md'
                    : 'text-foreground bg-card border border-primary/30 hover:border-primary'
                  }
                  ${!item?.unlocked ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-md">
                    <Icon name={item?.icon} size={24} />
                    <span>{item?.label}</span>
                  </div>
                  {!item?.unlocked && <Icon name="Lock" size={20} />}
                </div>
              </Link>
            ))}

            <div className="mt-auto pt-lg border-t border-primary/30">
              <Button
                variant="destructive"
                size="lg"
                iconName="LogOut"
                iconPosition="left"
                onClick={handleLogout}
                fullWidth
              >
                Logout
              </Button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default MainNavigation;